/** @typedef {Object} alkGeocodeService
 *  @property {Function} geocode
 *  @property {Function} reverseGeocode
 */

(function () {
  "use strict";

  angular
    .module("alk.geocode")
    .service("alkGeocodeService", alkGeocodeService);

  /** @name alkGeocodeService
   * @ngdoc service
   * @param {service} ALKMaps [ALKMaps](https://maps.alk.com/Developer/1.2/api_docs)
   * @param {service} $q angularJS [promises/deferred objects service](https://docs.angularjs.org/api/ng/service/$q)
   * @param {service} timeout angularJS [window.setTimeout wrapper](https://docs.angularjs.org/api/ng/service/$timeout#!)
   * @description Provides geocoding and reverse geocoding functions as a light wrapper around [ALKMaps](https://maps.alk.com/Developer/1.2/api_docs)
   * @returns alkGeocodeService
   * @memberof alk.geocode
   */

  alkGeocodeService.$inject = ["ALKMaps", "$q", "$timeout"];
  function alkGeocodeService(ALKMaps, $q, $timeout) {
    return {
      geocode: geocode,
      reverseGeocode: reverseGeocode
    };

    ////////////////

    /** geocode - Turn an address object into an array of Addresses. By default, we return one address unless
     * options.listSize > 1.
     * @param {Object} address - The input address to geocode. Should look like `{addr: string, city: string, state: string, zip: string, region: string}`
     * @param {Object} options - Options for geocoding. Supports `{listSize: int, region: string, dataset: string}`.
     * Valid values for region are NA, EU, OC, and SA. Valid values for dataset are PCM_EU, PCM_OC, PCM_SA, or Current.
     * @returns {Promise} Returns a [$q](https://docs.angularjs.org/api/ng/service/$q) promise that resolves to an array of address objects or
     * rejects to an object `{searchError: boolean, messages: [String]}`.
     * @memberof alkGeocodeService
     */
    function geocode(address, options) {
      var deferred = $q.defer();

      options = options || {};

      address.region = options.region || "NA";
      address.dataset = options.dataset || "PCM_NA";

      ALKMaps.Geocoder.geocode({
        address: address,
        listSize: options.listSize || 1, //Optional. The number of results returned if the geocoding service finds multiple matches.
        success: function (resp) {
          $timeout(function () {
            var chk = checkResponseError(resp);
            if (chk.hasError === true) {
              deferred.reject({
                searchError: true,
                messages: chk.messages
              });
            } else {
              deferred.resolve(resp);
            }
          });
        },
        failure: function () {
          $timeout(function () {
            deferred.reject({
              searchError: true
            });
          });
        }
      });

      return deferred.promise;
    }

    /** reverseGeocode - Turn a longitude and a latitude into an address.
     * @param {Object} lonLat - An [ALKMaps.LonLat](https://maps.alk.com/api/1.2/docs/files/BaseTypes/LonLat-js.html) or an object with lon and lat properties.
     * @param {Object} optsObj - Options for reverse geocoding. Supports region: string, dataset: string.
     * Valid values for region are NA, EU, OC, and SA. Valid values for dataset are PCM_EU, PCM_OC, PCM_SA, or Current.
     * @returns {Promise} Returns a [$q](https://docs.angularjs.org/api/ng/service/$q) promise that resolves to an array of address objects or
     * rejects to an object `{searchError: boolean, messages: [String]}`.
     * @memberof alkGeocodeService
     */
    function reverseGeocode(lonLat, optsObj) {
      var deferred = $q.defer();

      optsObj = optsObj || {};

      ALKMaps.Geocoder.reverseGeocode({
        lonLat: lonLat,
        region: optsObj.region || "NA",
        dataset: optsObj.dataset || "PCM_NA",
        success: function (resp) {
          $timeout(function () {
            var chk = checkResponseError(resp);

            if (chk.hasError === true) {
              deferred.reject({
                searchError: true,
                messages: chk.messages
              });
            } else {
              deferred.resolve(resp);
            }
          });
        },
        failure: function () {
          $timeout(function () {
            deferred.reject({
              searchError: true
            });
          });
        }
      });

      return deferred.promise;
    }

    function checkResponseError(resp) {
      var hasError = false;
      var messages = [];
      if (resp !== null && resp.length > 0) {
        var errors = resp[0].Errors; // Errors in first item
        if (errors !== null && errors.length > 0) {
          for (var i = 0; i < errors.length; i++) {
            if (errors[i].Type === 1) {
              hasError = true;
              messages.push(errors[i].Description);
            }
          }
        }
      }
      return {
        hasError: hasError,
        messages: messages
      };
    }
  }
})();



// WEBPACK FOOTER //
// ./~/@trimblemaps/webtools-common/javascript/AngularJS/alk-geocode/geocode.service.js